import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  MessageSquare, Plus, Star, Trash2, ChevronLeft,
  Smile, Meh, Frown, User, MapPin, Phone, BarChart3,
} from "lucide-react";
import { Link } from "react-router-dom";

const empty = {
  source_type: "customer",
  person_name: "",
  person_phone: "",
  location: "",
  subject_type: "service",
  subject_ref: "",
  rating: 5,
  comment: "",
  sentiment: "positive",
};

const SUBJECT_LABEL = {
  product: "Product", service: "Service", provider: "Provider", platform: "Platform",
};
const SENTIMENT_META = {
  positive: { icon: Smile, color: "text-green-600", bg: "bg-green-50" },
  neutral: { icon: Meh, color: "text-brand-yellow", bg: "bg-brand-yellow/10" },
  negative: { icon: Frown, color: "text-brand-orange", bg: "bg-brand-orange/10" },
};

export default function AdminFeedback() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState({ source: "", subject: "", sentiment: "" });
  const [form, setForm] = useState(empty);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadStats = () => api.get("/feedback/stats").then(({ data }) => setStats(data));
  const load = () => {
    const params = {};
    if (filter.source) params.source = filter.source;
    if (filter.subject) params.subject = filter.subject;
    if (filter.sentiment) params.sentiment = filter.sentiment;
    api.get("/feedback", { params }).then(({ data }) => setItems(data));
  };

  useEffect(() => { loadStats(); }, []);
  useEffect(() => { load(); }, [filter]);

  const save = async () => {
    if (!form.person_name.trim() || !form.comment.trim()) {
      toast.error("Name and comment are required");
      return;
    }
    setLoading(true);
    try {
      await api.post("/feedback", { ...form, rating: parseInt(form.rating) });
      toast.success("Feedback recorded");
      setOpen(false);
      setForm(empty);
      load();
      loadStats();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this feedback entry?")) return;
    try { await api.delete(`/feedback/${id}`); toast.success("Deleted"); load(); loadStats(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-feedback-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Link to="/admin" className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground">
            <ChevronLeft size={12} /> Back to admin
          </Link>
          <h1 className="mt-2 font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">Feedback log</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Oral feedback collected by franchisees from customers &amp; non-customers.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/analytics" data-testid="analytics-link">
            <Button variant="outline" className="rounded-full">
              <BarChart3 size={14} className="mr-1.5" /> Analytics
            </Button>
          </Link>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-brand-orange text-white hover:bg-brand-orange/90" data-testid="new-feedback-btn">
              <Plus size={16} className="mr-1.5" /> Record feedback
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Record oral feedback</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Source</Label>
                  <Select value={form.source_type} onValueChange={(v) => setForm({ ...form, source_type: v })}>
                    <SelectTrigger data-testid="fb-source"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="customer">Existing customer</SelectItem>
                      <SelectItem value="non_customer">Non-customer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Subject</Label>
                  <Select value={form.subject_type} onValueChange={(v) => setForm({ ...form, subject_type: v })}>
                    <SelectTrigger data-testid="fb-subject"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="product">Product</SelectItem>
                      <SelectItem value="service">Service</SelectItem>
                      <SelectItem value="provider">Provider</SelectItem>
                      <SelectItem value="platform">Platform overall</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1">
                <Label>Person's name</Label>
                <Input value={form.person_name} onChange={(e) => setForm({ ...form, person_name: e.target.value })} placeholder="e.g. Anjali M." data-testid="fb-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Phone (optional)</Label>
                  <Input value={form.person_phone} onChange={(e) => setForm({ ...form, person_phone: e.target.value })} placeholder="+91 ..." data-testid="fb-phone" />
                </div>
                <div className="space-y-1">
                  <Label>Location</Label>
                  <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="City / Area" data-testid="fb-location" />
                </div>
              </div>
              <div className="space-y-1">
                <Label>Subject reference (optional)</Label>
                <Input value={form.subject_ref} onChange={(e) => setForm({ ...form, subject_ref: e.target.value })} placeholder="Category / service / provider name" data-testid="fb-ref" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Rating (1-5)</Label>
                  <div className="flex gap-1" data-testid="fb-rating">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        type="button" key={n}
                        onClick={() => setForm({ ...form, rating: n })}
                        className="p-1"
                        data-testid={`fb-star-${n}`}
                      >
                        <Star size={22} className={n <= form.rating ? "fill-brand-yellow text-brand-yellow" : "text-muted-foreground"} />
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-1">
                  <Label>Sentiment</Label>
                  <Select value={form.sentiment} onValueChange={(v) => setForm({ ...form, sentiment: v })}>
                    <SelectTrigger data-testid="fb-sentiment"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="positive">Positive</SelectItem>
                      <SelectItem value="neutral">Neutral</SelectItem>
                      <SelectItem value="negative">Negative</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-muted-foreground">Leave as-is or clear to auto-detect via AI on save.</p>
                </div>
              </div>
              <div className="space-y-1">
                <Label>What did they say?</Label>
                <Textarea rows={4} value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} placeholder="Transcribe the customer's spoken feedback…" data-testid="fb-comment" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={save} disabled={loading} className="rounded-full bg-brand-orange text-white hover:bg-brand-orange/90" data-testid="save-feedback">
                {loading ? "Saving…" : "Save feedback"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total entries" value={stats.total || 0} icon={MessageSquare} color="#0A0A0A" />
        <StatCard label="Avg rating" value={(stats.avg_rating ?? 0).toFixed(1)} icon={Star} color="#FFD600" />
        <StatCard label="Customer / non-customer"
          value={`${stats.by_source?.customer || 0} / ${stats.by_source?.non_customer || 0}`}
          icon={User} color="#00E5FF" />
        <StatCard label="Pos / Neu / Neg"
          value={`${stats.by_sentiment?.positive || 0} / ${stats.by_sentiment?.neutral || 0} / ${stats.by_sentiment?.negative || 0}`}
          icon={Smile} color="#16a34a" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2" data-testid="fb-filters">
        <Chip active={!filter.source} onClick={() => setFilter({ ...filter, source: "" })}>All sources</Chip>
        <Chip active={filter.source === "customer"} onClick={() => setFilter({ ...filter, source: "customer" })}>Customer</Chip>
        <Chip active={filter.source === "non_customer"} onClick={() => setFilter({ ...filter, source: "non_customer" })}>Non-customer</Chip>
        <div className="mx-2 w-px bg-foreground/10" />
        <Chip active={!filter.subject} onClick={() => setFilter({ ...filter, subject: "" })}>All subjects</Chip>
        {["product", "service", "provider", "platform"].map((s) => (
          <Chip key={s} active={filter.subject === s} onClick={() => setFilter({ ...filter, subject: s })}>{SUBJECT_LABEL[s]}</Chip>
        ))}
      </div>

      {/* List */}
      <div className="space-y-3">
        {items.length === 0 ? (
          <div className="rounded-2xl border border-dashed p-10 text-center text-sm text-muted-foreground">
            No feedback recorded yet. Tap <b>Record feedback</b> to log one.
          </div>
        ) : items.map((f) => {
          const S = SENTIMENT_META[f.sentiment] || SENTIMENT_META.neutral;
          const Icon = S.icon;
          return (
            <div key={f.id} className="rounded-2xl border border-foreground/10 bg-white p-4" data-testid={`fb-row-${f.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`inline-flex items-center gap-1 rounded-full ${S.bg} px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${S.color}`}>
                      <Icon size={12} /> {f.sentiment || "neutral"}
                    </span>
                    <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest">{SUBJECT_LABEL[f.subject_type]}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${f.source_type === "customer" ? "bg-brand-cyan/20" : "bg-brand-yellow/20"}`}>
                      {f.source_type === "customer" ? "Customer" : "Non-customer"}
                    </span>
                    <span className="flex items-center gap-0.5 text-xs font-semibold">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star key={n} size={12} className={n <= f.rating ? "fill-brand-yellow text-brand-yellow" : "text-muted-foreground"} />
                      ))}
                    </span>
                  </div>
                  <div className="mt-2 font-heading text-base font-bold">{f.person_name}</div>
                  <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    {f.person_phone && <span className="flex items-center gap-1"><Phone size={10} /> {f.person_phone}</span>}
                    {f.location && <span className="flex items-center gap-1"><MapPin size={10} /> {f.location}</span>}
                    {f.subject_ref && <span>Ref: <b>{f.subject_ref}</b></span>}
                    <span>Collected by <b>{f.collector_name}</b></span>
                    <span>{new Date(f.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span>
                  </div>
                  <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-foreground/85">{f.comment}</p>
                </div>
                <Button variant="outline" size="sm" onClick={() => del(f.id)} data-testid={`fb-del-${f.id}`}>
                  <Trash2 size={12} className="text-brand-orange" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const StatCard = ({ label, value, icon: Icon, color }) => (
  <div className="rounded-2xl border border-foreground/10 bg-white p-4">
    <div className="flex items-center justify-between">
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className="mt-1 font-heading text-xl font-extrabold tracking-tight">{value}</div>
      </div>
      <div className="grid h-9 w-9 place-items-center rounded-xl text-white" style={{ background: color }}>
        <Icon size={14} />
      </div>
    </div>
  </div>
);

const Chip = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${active ? "border-brand-ink bg-brand-ink text-white" : "border-foreground/10 bg-white text-foreground/70"}`}
  >
    {children}
  </button>
);
